import { MULTIPLE_CHOICE, NOTE_IDENTIFICATION } from '@cavaquinho/shared';
import type { MultipleChoiceConfig, NoteIdentificationConfig } from '../../lib/api/types.js';
import { Text } from '../../ui/primitives/Text.tsx';
import { ConfigFields, emptyNoteIdentificationConfig } from './note-identification/ConfigFields.tsx';
import {
  ConfigFields as MultipleChoiceFields,
  emptyMultipleChoiceConfig,
} from './multiple-choice/ConfigFields.tsx';
import {
  validateMultipleChoiceConfig,
  validateNoteIdentificationConfig,
  type FieldViolation,
} from './validation.js';

/**
 * The type-agnostic configuration form (T100; SC-009, FR-040).
 *
 * Everything the admin UI knows about a particular Exercise Type is behind
 * this one lookup. The Lesson editor renders `<ExerciseConfigForm>` and never
 * mentions notes, pools or pitches; this file maps a type key to the fields
 * that type authors, the configuration a new one starts from, and the client
 * mirror of its validation rules.
 *
 * ADDING A TYPE IS ONE ENTRY IN THE TABLE BELOW plus the fields component it
 * names — no change to the Lesson editor, the list, the API client, or the
 * data layer. That is the admin half of SC-009, and the server half is
 * `GET /api/admin/exercise-types`, which derives its whole body from the
 * registry with no per-type branch at all.
 *
 * A type the server offers but this build has no form for renders as an
 * explicit, readable refusal rather than a blank panel: an administrator
 * seeing nothing cannot tell a missing form from a broken one.
 */

export interface ExerciseTypeForm {
  /** The fields this type authors. */
  readonly Fields: (props: {
    value: never;
    violations: readonly FieldViolation[];
    onChange: (next: never) => void;
  }) => React.ReactElement;
  /** What a newly added Exercise of this type starts from. */
  readonly empty: () => unknown;
  /** The client mirror of the server's rules — never the enforcement point. */
  readonly validate: (config: unknown) => FieldViolation[];
  /** Shown wherever a type is named to a person. */
  readonly label: string;
}

const FORMS: Record<string, ExerciseTypeForm> = {
  [NOTE_IDENTIFICATION]: {
    Fields: ConfigFields as unknown as ExerciseTypeForm['Fields'],
    empty: emptyNoteIdentificationConfig,
    validate: (config) =>
      validateNoteIdentificationConfig((config ?? {}) as Partial<NoteIdentificationConfig>),
    label: 'Identificação de nota',
  },
  [MULTIPLE_CHOICE]: {
    Fields: MultipleChoiceFields as unknown as ExerciseTypeForm['Fields'],
    empty: emptyMultipleChoiceConfig,
    validate: (config) =>
      validateMultipleChoiceConfig((config ?? {}) as Partial<MultipleChoiceConfig>),
    label: 'Múltipla escolha',
  },
};

export function formFor(exerciseType: string): ExerciseTypeForm | null {
  return FORMS[exerciseType] ?? null;
}

/** The type keys this build can author, for the "add an Exercise" control. */
export function authorableTypes(): string[] {
  return Object.keys(FORMS);
}

export function labelForType(exerciseType: string): string {
  return FORMS[exerciseType]?.label ?? exerciseType;
}

/** Validates through the type's own mirror; an unknown type has no client rules. */
export function validateConfigFor(exerciseType: string, config: unknown): FieldViolation[] {
  return formFor(exerciseType)?.validate(config) ?? [];
}

interface ExerciseConfigFormProps {
  exerciseType: string;
  config: unknown;
  violations: readonly FieldViolation[];
  onChange: (next: unknown) => void;
}

export function ExerciseConfigForm({
  exerciseType,
  config,
  violations,
  onChange,
}: ExerciseConfigFormProps) {
  const form = formFor(exerciseType);

  if (!form) {
    return (
      <div data-testid="unknown-exercise-type">
        <Text as="p" variant="body-sm" tone="error">
          Esta versão do painel não sabe configurar exercícios do tipo "{exerciseType}".
        </Text>
        <Text as="p" variant="body-sm" tone="muted">
          O exercício continua funcionando para quem pratica; apenas a edição não está disponível
          aqui.
        </Text>
      </div>
    );
  }

  const { Fields } = form;
  return (
    <Fields
      value={config as never}
      violations={violations}
      onChange={onChange as (next: never) => void}
    />
  );
}
